((scope) => {
  'use strict';

  const EVENT_FIELDS = Object.freeze({
    view_item: ['product_id', 'variant_id', 'currency', 'unit_price_minor'],
    select_item: ['product_id', 'variant_id', 'source'],
    begin_personalization: ['product_id', 'variant_id'],
    option_change: ['product_id', 'variant_id', 'option'],
    preview_success: ['product_id', 'variant_id', 'renderer', 'latency_ms', 'cache'],
    preview_error: ['product_id', 'variant_id', 'reason'],
    add_to_cart: ['product_id', 'variant_id', 'currency', 'unit_price_minor', 'quantity'],
    begin_checkout: ['currency', 'value_minor', 'item_count']
  });
  const SOURCES = new Set(['home', 'collection', 'search', 'cart', 'product', 'saved', 'editorial', 'finder']);
  const OPTIONS = new Set(['material', 'chain', 'length', 'font', 'quantity']);
  const ERRORS = new Set(['unsupported', 'unavailable', 'timeout', 'network', 'invalid_response', 'stale']);
  const SORTS = new Set(['manual', 'best-selling', 'title-ascending', 'title-descending', 'price-ascending', 'price-descending', 'created-ascending', 'created-descending', 'relevance']);
  const MAX_SEEN = 128;
  const DEDUP_MS = 300000;

  function id(value) {
    if (typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
    return typeof value === 'string' && /^[1-9]\d{0,19}$/.test(value) ? value : null;
  }

  function integer(value, min, max) {
    return Number.isSafeInteger(value) && value >= min && value <= max;
  }

  function sanitizeEvent(name, input) {
    try {
      if (typeof name !== 'string' || !Object.hasOwn(EVENT_FIELDS, name) || !input || typeof input !== 'object') return null;
      const prototype = Object.getPrototypeOf(input);
      if (prototype !== Object.prototype && prototype !== null) return null;
      if (Object.getOwnPropertySymbols(input).length) return null;
      const descriptors = Object.getOwnPropertyDescriptors(input);
      const fields = EVENT_FIELDS[name];
      const keys = Object.keys(descriptors);
      if (keys.length !== fields.length || keys.some(key => !fields.includes(key))) return null;
      if (fields.some(key => !Object.hasOwn(descriptors[key] || {}, 'value'))) return null;
      const clean = {schema_version: 1};
      for (const key of fields) {
        const value = descriptors[key].value;
        if (key === 'product_id' || key === 'variant_id') {
          const normalized = id(value);
          if (!normalized) return null;
          clean[key] = normalized;
        } else if (key === 'currency') {
          if (typeof value !== 'string' || !/^[A-Z]{3}$/.test(value)) return null;
          clean[key] = value;
        } else if (key === 'unit_price_minor' || key === 'value_minor') {
          if (!integer(value, 0, 1000000000)) return null;
          clean[key] = value;
        } else if (key === 'quantity' || key === 'item_count') {
          if (!integer(value, 1, 999)) return null;
          clean[key] = value;
        } else if (key === 'latency_ms') {
          if (!integer(value, 0, 120000)) return null;
          clean[key] = value;
        } else if (key === 'source') {
          if (!SOURCES.has(value)) return null;
          clean[key] = value;
        } else if (key === 'option') {
          if (!OPTIONS.has(value)) return null;
          clean[key] = value;
        } else if (key === 'reason') {
          if (!ERRORS.has(value)) return null;
          clean[key] = value;
        } else if (key === 'renderer') {
          if (value !== 'supplier_verified') return null;
          clean[key] = value;
        } else if (key === 'cache') {
          if (value !== 'cold' && value !== 'warm') return null;
          clean[key] = value;
        }
      }
      return Object.freeze(clean);
    } catch (_) {
      return null;
    }
  }

  function safeContextUrl(href) {
    try {
      if (typeof href !== 'string') return false;
      const url = new URL(href);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash) return false;
      const keys = new Set();
      for (const [key, value] of url.searchParams) {
        if (keys.has(key)) return false;
        keys.add(key);
        if (key === 'variant' && id(value)) continue;
        if (key === 'page' && /^[1-9]\d{0,4}$/.test(value)) continue;
        if (key === 'new_piece' && value === '1') continue;
        if (key === 'sort_by' && SORTS.has(value)) continue;
        return false;
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  function createClient({enabled, suppressed, consent, publish, clock = Date.now} = {}) {
    const seen = new Map();
    let occurrences = new WeakMap();
    let sequence = 0;
    let previousTime = 0;
    let destroyed = false;

    function reset() {
      seen.clear();
      occurrences = new WeakMap();
    }

    function permitted() {
      try {
        if (destroyed || enabled?.() !== true || suppressed?.() !== false) return false;
        const state = consent?.();
        return state?.allowed === true && state?.recorded === 'yes';
      } catch (_) {
        return false;
      }
    }

    function newOccurrence() {
      if (destroyed) return null;
      const token = Object.freeze({});
      occurrences.set(token, ++sequence);
      return token;
    }

    async function track(name, input, occurrence) {
      if (!permitted()) {
        reset();
        return 'suppressed';
      }
      const payload = sanitizeEvent(name, input);
      if (!payload) return 'invalid';
      let key;
      if (name === 'view_item') key = `${name}:${payload.product_id}:${payload.variant_id}`;
      else if (name === 'begin_personalization') key = `${name}:${payload.product_id}`;
      else {
        if (!occurrence || !occurrences.has(occurrence)) return 'invalid_occurrence';
        key = `${name}:${occurrences.get(occurrence)}`;
      }
      let now;
      try { now = clock(); } catch (_) { return 'unavailable'; }
      if (!Number.isFinite(now) || now < 0) return 'unavailable';
      if (now < previousTime) seen.clear();
      previousTime = now;
      for (const [priorKey, time] of seen) if (now - time >= DEDUP_MS) seen.delete(priorKey);
      if (seen.has(key)) return 'duplicate';
      if (typeof publish !== 'function') return 'unavailable';
      seen.set(key, now);
      while (seen.size > MAX_SEEN) seen.delete(seen.keys().next().value);
      try {
        const result = await publish(`auriani:${name}`, payload);
        return result === false ? 'rejected' : 'submitted';
      } catch (_) {
        return 'failed';
      }
    }

    return Object.freeze({
      track,
      newOccurrence,
      reset,
      consentChanged() { if (!permitted()) reset(); },
      destroy() { destroyed = true; reset(); }
    });
  }

  function createShopifyClient(windowScope) {
    const document = windowScope?.document;
    const client = createClient({
      enabled: () => document?.body?.dataset.analyticsEnabled === 'true',
      suppressed: () => {
        const data = document?.body?.dataset;
        if (!data || data.previewMode !== 'false' || data.qaMode !== 'false') return true;
        if (data.analyticsEnvironment !== 'production' || data.themeRole !== 'main') return true;
        if (data.analyticsContextReviewed !== 'true' || windowScope.Shopify?.designMode) return true;
        if (!safeContextUrl(windowScope.location?.href)) return true;
        return Boolean(document.referrer && !safeContextUrl(document.referrer));
      },
      consent: () => {
        const privacy = windowScope.Shopify?.customerPrivacy;
        return {
          allowed: privacy?.analyticsProcessingAllowed?.(),
          recorded: privacy?.currentVisitorConsent?.()?.analytics
        };
      },
      publish: (name, payload) => {
        const analytics = windowScope.Shopify?.analytics;
        if (typeof analytics?.publish !== 'function') return false;
        return analytics.publish(name, payload);
      }
    });
    const changed = () => client.consentChanged();
    document?.addEventListener?.('visitorConsentCollected', changed);
    return Object.freeze({
      track: client.track,
      newOccurrence: client.newOccurrence,
      reset: client.reset,
      destroy() {
        document?.removeEventListener?.('visitorConsentCollected', changed);
        client.destroy();
      }
    });
  }

  const api = Object.freeze({createClient, createShopifyClient, sanitizeEvent, safeContextUrl});
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (scope?.document && !scope.AurianiAnalytics) scope.AurianiAnalytics = createShopifyClient(scope);
})(typeof window !== 'undefined' ? window : undefined);
